import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CommitteeList, DirectoryOtherViewModel, ExecutiveViewModel, Member, MembersList } from '../../shared/models/directory-models';
import { Branch } from '../../shared/enums/branch.enum';
import { Observable, of, interval } from 'rxjs';
import { catchError, shareReplay, startWith, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class DirectoryService {
  private directoryApiBaseUrl = 'api/Directory';

  private cachedMembers$: Observable<MembersList[]>;
  private cachedLeadership$: { [branch: number]: Observable<Member[]> } = {};
  private cachedExecs$: Observable<ExecutiveViewModel[]>;
  private cachedHouse$: Observable<CommitteeList[]>;
  private cachedSenate$: Observable<CommitteeList[]>;
  private cachedJoint$: Observable<CommitteeList[]>;
  private cachedOthers$: Observable<DirectoryOtherViewModel[]>;

  constructor(private httpClient: HttpClient) { }

  clearCache(): void {
    this.cachedMembers$ = null;
    for (const branch in this.cachedLeadership$) {
      if (this.cachedLeadership$.hasOwnProperty(branch)) {
        this.cachedLeadership$[branch] = null;
      }
    }
    this.cachedExecs$ = null;
    this.cachedHouse$ = null;
    this.cachedSenate$ = null;
    this.cachedJoint$ = null;
    this.cachedOthers$ = null;
  }

  getMembers(branch: Branch): Observable<Member[]> {
    if (!this.cachedLeadership$[branch]) {
      this.cachedLeadership$[branch] = interval(5 * 60_000).pipe(
        startWith(0),
        switchMap(() =>
          this.httpClient.get<Member[]>(`${this.directoryApiBaseUrl}/Members/${branch}`).pipe(
            catchError(error => {
              console.error(`Error fetching members for branch ${branch}:`, error);
              return of<Member[]>([]);
            })
          )
        ),
        shareReplay(1)
      );
    }
    return this.cachedLeadership$[branch];
  }

  getAllExecs(): Observable<ExecutiveViewModel[]> {
    if (!this.cachedExecs$) {
      this.cachedExecs$ = interval(5 * 60_000).pipe(
        startWith(0),
        switchMap(() =>
          this.httpClient.get<ExecutiveViewModel[]>(`${this.directoryApiBaseUrl}/Executives`).pipe(
            catchError(error => {
              console.error('Error fetching all executives:', error);
              return of<ExecutiveViewModel[]>([]);
            })
          )
        ),
        shareReplay(1)
      );
    }
    return this.cachedExecs$;
  }

  getAllHouseCommittees(): Observable<CommitteeList[]> {
    if (!this.cachedHouse$) {
      this.cachedHouse$ = interval(5 * 60_000).pipe(
        startWith(0),
        switchMap(() =>
          this.httpClient.get<CommitteeList[]>(`${this.directoryApiBaseUrl}/HouseCommittees`).pipe(
            catchError(error => {
              console.error('Error fetching all House committees:', error);
              return of<CommitteeList[]>([]);
            })
          )
        ),
        shareReplay(1)
      );
    }
    return this.cachedHouse$;
  }

  getAllSenateCommittees(): Observable<CommitteeList[]> {
    if (!this.cachedSenate$) {
      this.cachedSenate$ = interval(5 * 60_000).pipe(
        startWith(0),
        switchMap(() =>
          this.httpClient.get<CommitteeList[]>(`${this.directoryApiBaseUrl}/SenateCommittees`).pipe(
            catchError(error => {
              console.error('Error fetching all Senate committees:', error);
              return of<CommitteeList[]>([]);
            })
          )
        ),
        shareReplay(1)
      );
    }
    return this.cachedSenate$;
  }

  getAllJointCommittees(): Observable<CommitteeList[]> {
    if (!this.cachedJoint$) {
      this.cachedJoint$ = interval(5 * 60_000).pipe(
        startWith(0),
        switchMap(() =>
          this.httpClient.get<CommitteeList[]>(`${this.directoryApiBaseUrl}/JointCommittees`).pipe(
            catchError(error => {
              console.error('Error fetching all Joint committees:', error);
              return of<CommitteeList[]>([]);
            })
          )
        ),
        shareReplay(1)
      );
    }
    return this.cachedJoint$;
  }

  getAllOthers(): Observable<DirectoryOtherViewModel[]> {
    if (!this.cachedOthers$) {
      this.cachedOthers$ = interval(5 * 60_000).pipe(
        startWith(0),
        switchMap(() =>
          this.httpClient.get<DirectoryOtherViewModel[]>(`${this.directoryApiBaseUrl}/Others`).pipe(
            catchError(error => {
              console.error('Error fetching all other members:', error);
              return of<DirectoryOtherViewModel[]>([]);
            })
          )
        ),
        shareReplay(1)
      );
    }
    return this.cachedOthers$;
  }
}
